import { createApp } from 'vue';
import Popify from '@thehotelsnetwork/personalization-popify/dist/popify.es';
import css from '@thehotelsnetwork/personalization-popify/dist/popify.styles';
import { AbortablePromise, AbortError } from './main.js';
import { createRootContainer, insertRootContainer } from './chunk-insert-root-container.js';
import { createPcomponentParam } from './chunk-create-pcomponent-param.js';
import './chunk-widgets.js';
import '@thehotelsnetwork/md5';

const factory = ({ document, thn, }) => {
    const viewData = {};
    const widgetView = {
        data: viewData,
        hasFailed() {
            return !!viewData.error;
        },
        async create(ctx) {
            if (viewData.el) {
                return;
            }
            const { bundle, debugPrefix } = ctx;
            const zIndex = bundle.zIndex ? String(-1 * Number(bundle.zIndex)) : 'auto';
            const styleProps = {
                'background-color': 'transparent',
                display: 'block',
                float: 'none',
                margin: '0',
                padding: '0',
                border: '0',
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                visibility: 'hidden',
                'z-index': zIndex,
            };
            const { shadowEl, topElement, shadowRoot } = createRootContainer({
                id: this.data.id,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                contentStyles: css,
                topElementStyles: styleProps,
            }, { document });
            viewData.el = topElement;
            viewData.shadowRoot = shadowRoot;
            viewData.shadowEl = shadowEl;
            topElement.setAttribute('data-thn-visible', '0');
            const insertArgs = document.body.lastElementChild
                ? [document.body.lastElementChild, topElement, 'IA']
                : [document.body, topElement, 'II'];
            const isRootInserted = insertRootContainer(...insertArgs);
            if (!isRootInserted) {
                const error = new Error('Failed to insert the root container in the DOM');
                this.logger.warn(debugPrefix, error.message, 'with args:', insertArgs);
                error.status = 'no-selector';
                viewData.error = error;
                throw error;
            }
            await this.container.scheduler.yield();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            viewData.app = createApp(Popify, {});
            viewData.vm = viewData.app.mount(shadowEl);
        },
        async show(ctx) {
            if (!viewData.el || !viewData.shadowEl) {
                await widgetView.create.call(this, ctx);
            }
            if (!viewData.el || !viewData.shadowEl) {
                return;
            }
            if (viewData.error && viewData.error.status) {
                widgetView.destroy();
                return;
            }
            const { bundle } = ctx;
            const fontsData = bundle.tool.getFontsData();
            if (fontsData) {
                const fontsLoader = await this.container.async.fontsLoader;
                const fontsPromise = AbortablePromise.from(fontsLoader.addFonts(fontsData, document));
                thn.pageUnload(() => fontsPromise.abort());
                try {
                    await fontsPromise;
                }
                catch (error) {
                    if (error instanceof AbortError) {
                        return;
                    }
                    throw error;
                }
            }
            viewData.el.style.visibility = 'visible';
            viewData.el.setAttribute('data-thn-visible', '1');
            if (bundle.tool.getContext().isDelegated) {
                viewData.el.style.zIndex = bundle.tool.getContext().zIndex;
            }
            const zIndex = Number(viewData.el.style.zIndex);
            if (zIndex < 0) {
                viewData.el.style.zIndex = `${-1 * zIndex}`;
            }
            bundle.modalRootContainer = viewData.shadowEl || viewData.el;
            const removePopup = viewData.vm?.addPopup(createPcomponentParam(bundle));
            if (removePopup) {
                this.onDestroy(removePopup);
            }
        },
        destroy() {
            if (viewData.error) {
                delete viewData.error;
            }
            if (viewData.el) {
                viewData.el.remove();
                delete viewData.el;
                delete viewData.shadowEl;
                delete viewData.shadowRoot;
            }
        },
    };
    return widgetView;
};

export { factory };
